/*
    Evidence for the hydration errors that keep arriving from /p/.

    React reports #418, #423 and #425 in production as minified codes with no diff: it will
    not say which text disagreed, so the fault list tells us a mismatch happened and nothing
    about what it was. Three rounds of fixes have each removed a real cause (#658, #674,
    #676) and the reports have continued, all from phones, on pages that demonstrably run
    injected code - fault 132546675 is an appendChild patched by something we do not ship.

    So the next occurrence brings its own evidence. A snapshot of the server's text is taken
    before hydration, and when a hydration error is reported the two are compared: the first
    place they differ, plus what the page looks like around it. That separates the two
    possibilities we cannot otherwise tell apart - our markup being wrong, or someone else
    rewriting it before React looked.

    Deliberately conservative about what leaves the browser: an invoice's text is a payer's
    name, address and what they bought, so the excerpts are short and run through a redactor
    for emails, long digit runs and money first. It answers with what it has and never
    throws; diagnostics that take a page down are worse than no diagnostics.
*/

// Where the snapshot lives between page load and an error, if one comes.
const BASELINE_KEY = '__SSR_TEXT_BASELINE__';

// Enough to find the mismatch, small enough to keep out of the way.
const MAX_BASELINE = 64 * 1024;
const EXCERPT = 90;

/*
    Runs between the server's markup and Next's bundles, which are deferred, so it sees the
    page exactly as it arrived and before React touches it. Inline and tiny for the same
    reason: it is on the critical path of every page.
*/
export const hydrationBaselineScript = () => `try{var r=document.getElementById('__next')||document.body,w=document.createTreeWalker(r,NodeFilter.SHOW_TEXT),t='',n;while((n=w.nextNode())&&t.length<${MAX_BASELINE}){var p=n.parentNode&&n.parentNode.nodeName;if(p!=='SCRIPT'&&p!=='STYLE')t+=n.nodeValue}window.${BASELINE_KEY}={text:t.slice(0,${MAX_BASELINE}),bodyChildren:document.body.children.length,at:Date.now()}}catch(e){}`;

/*
    The text a person would read, which is what the two renders have to agree on. Script and
    style contents are skipped: next-themes renders its bootstrap script inside the app root,
    and while both renders carry it identically, it is several hundred characters of noise in
    front of every excerpt. Read the same way on both sides so the comparison lines up.
*/
const visibleText = (root, doc) => {
    const walker = doc.createTreeWalker(root, 4 /* SHOW_TEXT */);
    let text = '';
    let node = walker.nextNode();
    while (node && text.length < MAX_BASELINE) {
        const parent = node.parentNode && node.parentNode.nodeName;
        if (parent !== 'SCRIPT' && parent !== 'STYLE') text += node.nodeValue;
        node = walker.nextNode();
    }
    return text.slice(0, MAX_BASELINE);
};

// An invoice names people. Nothing that identifies one should ride along in an error report.
export const redact = (value) => String(value || '')
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '<email>')
    .replace(/\d[\d.,]{3,}/g, '<number>')
    .replace(/\s+/g, ' ')
    .trim();

const isNative = (fn) => {
    try { return /\{\s*\[native code\]\s*\}/.test(Function.prototype.toString.call(fn)); } catch (e) { return true; }
};

/*
    Marks of code we do not ship, each one seen in the wild on pages like these: a translator
    rewriting text nodes, an in-app browser or extension patching the DOM methods React
    builds through, or scripts and frames inserted into the page.
*/
/*
    Split in two on purpose. Only the signals that cannot be us decide whether a report is
    dropped: a translated page, a DOM method replaced, a script from a host we never load.
    Everything else is context for a person reading the fault.

    Counting the children of <body> belongs in the second group, however tempting the first
    looks: Grommet mounts its layers there and Next its route announcer and dev overlay, so
    a page of ours grows extra children all by itself. A signal that drops real reports is
    worse than no signal.
*/
const tamperSignals = (doc, win) => {
    const signals = {};
    const root = doc.documentElement;

    const translated = ['translated-ltr', 'translated-rtl'].filter((c) => root.classList.contains(c));
    if (translated.length) signals.translated = translated.join(',');
    if (doc.querySelector('.goog-te-banner-frame, #google_translate_element, .goog-te-combo')) signals.translateWidget = true;

    /*
        Reading a method's source is the only way to ask whether something replaced it. If
        every one of them reads as non-native the answer is not "all four were patched", it is
        that this environment does not implement them natively at all - jsdom does not - and
        the signal says nothing. Calling that tampering would drop real reports, which is the
        one mistake this must not make. A page where something patched all four goes unnoticed
        instead; the fault we have seen patches one.
    */
    const methods = [
        ['appendChild', win.Node && win.Node.prototype.appendChild],
        ['insertBefore', win.Node && win.Node.prototype.insertBefore],
        ['removeChild', win.Node && win.Node.prototype.removeChild],
        ['setAttribute', win.Element && win.Element.prototype.setAttribute],
    ].filter(([, fn]) => fn);
    const patched = methods.filter(([, fn]) => !isNative(fn)).map(([name]) => name);
    if (patched.length && patched.length < methods.length) signals.patchedDomMethods = patched.join(',');

    // Our own scripts and the three third parties the app loads on purpose.
    const expected = /(^|\.)(streamlinedpayments\.com|segment\.(io|com)|stripe\.com|honeybadger\.io)$/;
    const foreign = [...doc.querySelectorAll('script[src]')]
        .map((s) => { try { return new win.URL(s.src, win.location.href).hostname; } catch (e) { return null; } })
        .filter((host) => host && host !== win.location.hostname && !expected.test(host));
    if (foreign.length) signals.foreignScripts = [...new Set(foreign)].slice(0, 5).join(',');

    return signals;
};

// Context only: see the note above tamperSignals for why this never decides anything.
const bodyNeighbours = (doc) => [...doc.body.children]
    .filter((el) => el.id !== '__next' && el.tagName !== 'SCRIPT')
    .map((el) => el.tagName.toLowerCase())
    .slice(0, 8)
    .join(',');

/*
    Everywhere the page stopped matching what the server sent, not just the first.

    The first difference is nearly always ours and nearly always uninteresting: LocalDate
    ships a UTC placeholder and swaps in the reader's date after mount, so a page in Los
    Angeles reads "Oct 15" on the server and "Oct 14" a moment later, near the top of the
    invoice. Reporting only that would bury the difference worth seeing under one we put
    there on purpose. So it reports several, and whoever reads the fault can skip the dates.

    Deliberately simple. After a difference it re-synchronises on the next stretch of text
    the two sides agree on, which is enough to separate "a date moved" from "this sentence is
    in Spanish now" without shipping a diff algorithm to every payer.
*/
const ANCHOR = 24;
const MAX_SITES = 3;

const LEAD_IN = 24;

const findDifferences = (before, after) => {
    const sites = [];
    let i = 0;
    let j = 0;

    while (i < before.length && j < after.length && sites.length < MAX_SITES) {
        if (before[i] === after[j]) { i += 1; j += 1; continue; }

        // Lead-in included: a date reads as "Due Oct 15" rather than starting at the digit
        // that happens to differ.
        sites.push({
            atCharacter: i,
            server: redact(before.slice(Math.max(0, i - LEAD_IN), i + EXCERPT)),
            browser: redact(after.slice(Math.max(0, j - LEAD_IN), j + EXCERPT)),
        });

        /*
            Re-synchronise on the next stretch the two sides agree on. The anchor is taken
            past the window just reported, and a short one is still worth searching for:
            requiring a full-length anchor gave up whenever a difference sat near the end,
            which is where a payment instruction or a footer lives.
        */
        const anchorStart = Math.min(i + EXCERPT, before.length);
        const anchor = before.slice(anchorStart, anchorStart + ANCHOR);
        if (!anchor) break;
        const resumeAt = after.indexOf(anchor, j);
        if (resumeAt === -1) break;
        i = anchorStart;
        j = resumeAt;
    }

    return sites;
};

export const collectHydrationForensics = (win = typeof window !== 'undefined' ? window : undefined) => {
    try {
        if (!win || !win.document || !win.document.body) return null;
        const doc = win.document;
        const baseline = win[BASELINE_KEY];
        const signals = tamperSignals(doc, win);

        const forensics = {
            ...signals,
            tampered: Object.keys(signals).length > 0,
            baselineTaken: !!baseline,
            bodyNeighbours: bodyNeighbours(doc),
        };

        if (baseline) {
            const now = visibleText(doc.getElementById('__next') || doc.body, doc);
            forensics.msSinceBaseline = Date.now() - baseline.at;
            forensics.bodyChildrenThen = baseline.bodyChildren;
            forensics.bodyChildrenNow = doc.body.children.length;
            const differences = findDifferences(baseline.text, now);
            if (differences.length) forensics.differences = differences;
            forensics.textChars = now.length;
        }

        return forensics;
    } catch (e) {
        return { forensicsFailed: String((e && e.message) || e).slice(0, 80) };
    }
};

// A hydration error we can blame on code we do not ship.
export const looksTampered = (forensics) => !!(forensics && forensics.tampered);

export const REACT_HYDRATION_ERRORS = ['#418', '#423', '#425', 'Hydration failed', 'Text content does not match'];

export const isHydrationError = (message) => !!message && REACT_HYDRATION_ERRORS.some((m) => String(message).includes(m));
